import { useQuery } from '@apollo/client';
import { Box, Container, Flex, Input, Spinner, TextLink, theme } from '@energiebespaarders/symbols';
import { Center, Color, Medium, Small } from '@energiebespaarders/symbols/helpers';
import { Search } from '@energiebespaarders/symbols/icons/solid';
import { ListMagnifyingGlass } from '@phosphor-icons/react';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  constructUrl,
  GET_ALL_FAQ_ITEMS_AND_PARENT_SLUGS_BY_QUESTION,
} from '~/components/FAQ/FAQSearchResults';
import HybridLink from '~/components/HybridLink';
import { useDebouncedValue } from '~/hooks/useDebounce';
import useEventTracker from '~/hooks/useEventTracker';
import type {
  getAllFaqItemsAndParentSlugsByQuestion,
  getAllFaqItemsAndParentSlugsByQuestionVariables,
} from '~/types/generated/cms/getAllFaqItemsAndParentSlugsByQuestion';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import { StyledNavMenu } from './NavMenu';
import type { ChangeEvent, MutableRefObject } from 'react';

const StyledResult = styled(HybridLink)`
  color: ${x => x.theme.colors.grayDarker};
  display: block;
  transform: translateX(0);
  transition: transform 0.2s ${x => x.theme.curves.standard};

  &:hover {
    transform: translateX(3px);
    color: ${x => x.theme.colors.greenDark};
  }
`;

interface SearchMenuProps {
  handleClose: () => void;
}

const SearchMenu: React.FC<SearchMenuProps> = ({ handleClose }) => {
  const [searchString, setSearchString] = useState('');
  const router = useRouter();
  const goToFaq = useCallback(() => {
    handleClose();
    void router.push('/faq');
  }, [router, handleClose]);

  const searchInput = useRef<HTMLInputElement>();

  useEffect(() => {
    searchInput?.current?.focus();
  }, [searchInput]);

  const trackEvent = useEventTracker();
  const debouncedSearchString = useDebouncedValue(searchString, 2000);
  useEffect(() => {
    if (debouncedSearchString.length > 3) {
      void trackEvent('search', { search_term: debouncedSearchString });
    }
  }, [debouncedSearchString, trackEvent]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const cleanedString = e.target.value.replace(/[^\w\s]/g, '');
    setSearchString(cleanedString);
  };

  const datoClient = getDatoClient(false);

  const { data: matchedItems, loading } = useQuery<
    getAllFaqItemsAndParentSlugsByQuestion,
    getAllFaqItemsAndParentSlugsByQuestionVariables
  >(GET_ALL_FAQ_ITEMS_AND_PARENT_SLUGS_BY_QUESTION, {
    client: datoClient,
    variables: { question: searchString },
    skip: searchString.length < 3,
  });

  const searchResults = matchedItems
    ? [...matchedItems.allFaqItems, ...matchedItems.allFaqTopics]
    : [];
  return (
    <StyledNavMenu>
      <Container size="lg">
        <Flex
          flexWrap="wrap"
          justifyContent={['space-around', 'space-between', 'flex-end']}
          alignItems="center"
        >
          <Box width={1}>
            <Input
              innerRef={
                searchInput.current
                  ? (searchInput as MutableRefObject<HTMLInputElement>)
                  : undefined
              }
              value={searchString}
              onChange={handleSearch}
              addonContent={Search}
              icon={Search}
              addonSide="start"
              autoFocus
              inputMode="search"
              placeholder="Zoeken..."
              mb={2}
            />

            <Box width={1}>
              <Medium>
                {searchString.length < 3 ? 'Zoekresultaten' : `Zoekresultaten voor ${searchString}`}
              </Medium>
            </Box>

            {loading ? (
              <Center block>
                <Spinner />
              </Center>
            ) : searchString.length < 3 ? (
              <Color c="grayDark">Typ ten minste 3 karakters om te zoeken</Color>
            ) : searchResults.length <= 0 ? (
              <Color c="grayDark">Er zijn geen zoekresultaten gevonden</Color>
            ) : (
              <>
                {searchResults.slice(0, 5).map(item => {
                  const isFaqItem = item.__typename === 'FaqItemRecord';
                  return (
                    <Box key={item.id} py={1}>
                      <StyledResult
                        to={constructUrl(item)}
                        onClick={handleClose}
                        color="grayDarker"
                      >
                        {isFaqItem ? item.question : item.title}
                      </StyledResult>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
          <Box width={1} pt={2}>
            <TextLink onClick={goToFaq} title="Bekijk alle veelgestelde vragen" color="greenDarker">
              <Small>
                <ListMagnifyingGlass
                  weight="bold"
                  color={theme.colors.greenDark}
                  style={{ verticalAlign: 'text-bottom' }}
                />{' '}
                Bekijk alle veelgestelde vragen
              </Small>
            </TextLink>
          </Box>
        </Flex>
      </Container>
    </StyledNavMenu>
  );
};

export default SearchMenu;
